import React from 'react';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SearchIcon } from '../../../assets/SVG/Search-Icon.svg';
import PropTypes from 'prop-types';


export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  minWidth?: number | string;
  height?: number | string;
  inputAriaLabel?: string;
  icon?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}


/**
 * SearchBar is a robust, reusable search input with icon, error handling, and accessibility.
 */

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  error = false,
  minWidth = 380,
  height = 48,
  inputAriaLabel = 'search',
  icon,
  className,
  style,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const { t: tBilling } = useTranslation('billing_config');

  const searchId = 'invoice-search-field';
  const searchIconId = 'invoice-search-icon';

  return (
    <Box 
      component="section"
      display="flex" 
      alignItems="center" 
      gap={1} 
      className={className} 
      style={style}
      aria-label={tBilling('invoice.search.section_label')}
    >
      <Box
        component="fieldset"
        display="flex"
        alignItems="center"
        sx={{
          background: error ? '#fff0f0' : '#fff',
          border: error ? '1.5px solid #f44336' : '1.5px solid #E0E0E0',
          borderRadius: 2.5,
          pl: 2,
          pr: 1,
          height,
          minWidth,
          flex: 1,
        }}
      >
        <Box
          component="legend"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            border: 0,
            clip: 'rect(0 0 0 0)',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {tBilling('invoice.search.legend')}
        </Box>
        <Box 
          component="span" 
          id={searchIconId}
          sx={{ color: '#B0B0B0', fontSize: 22, mr: 1, display: 'flex', alignItems: 'center' }}
          aria-hidden="true"
        >
          {icon || <SearchIcon style={{ width: 20, height: 20 }} />}
        </Box>
        <InputBase
          id={searchId}
          data-testid="invoice-search-input"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          sx={{
            border: 'none',
            outline: 'none',
            fontSize: 16,
            flex: 1,
            background: 'transparent',
            color: '#333',
          }}
          slotProps={{
            input: {
              'aria-label': inputAriaLabel,
              'aria-describedby': error ? 'search-error' : undefined,
              role: 'searchbox',
            },
          }}
        />
        {value && !disabled && (
          <IconButton
            data-testid="invoice-search-clear"
            size="small"
            aria-label={tBilling('invoice.search.clear_label')}
            onClick={() => onChange('')}
            sx={{ ml: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {error && (
        <Box
          id="search-error"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            border: 0,
            clip: 'rect(0 0 0 0)',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {tBilling('invoice.search.error_message')}
        </Box>
      )}
    </Box>
  );
};

// PropTypes for JS usage and runtime validation
SearchBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inputAriaLabel: PropTypes.string,
  icon: PropTypes.element,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default SearchBar;





